import * as React from "react";
import { useTheme } from "next-themes";
import { Button, cn } from "@siena-ai/design-system";
import { Moon, Sun } from "lucide-react";

// Our own toggle that flips based on the *resolved* theme, not the raw setting.
// The DS ThemeToggle compares `theme === "dark"`, which is broken when the user
// starts on `theme: "system"` — the first click maps to "dark" (already rendered),
// so nothing visibly happens until the second click.
export const ThemeToggle: React.FC<{
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
  className?: string;
}> = ({ variant = "ghost", size = "icon-sm", className }) => {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === "dark";

  return (
    <Button
      variant={variant}
      size={size}
      className={cn(className)}
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      disabled={!mounted}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
};
